import React, { useEffect } from 'react'
import { Helmet } from 'react-helmet'
import { Navigate, Route, Routes, useNavigate } from 'react-router'

import { MobileReceiving } from '../warehouse/receiving'
import { RfShell } from '../warehouse/receiving/components/RfShell'
import { MenuButton, MenuList, TaskHeader } from '../warehouse/receiving/styles'

const useRfPageTitle = (title: string) => {
  useEffect(() => {
    document.title = `${title} | Kase`
  }, [title])
}

interface RfHomeProps {
  embedded?: boolean
  onOpenInbound?: () => void
  onExitRf?: () => void
}

// Presentational home shared by the live RF wrapper and inert dev snapshots.
export const RfHomeScreen = ({
  embedded = false,
  onOpenInbound,
  onExitRf,
}: {
  embedded?: boolean
  onOpenInbound: () => void
  onExitRf: () => void
}) => (
  <RfShell
    title="RF Home"
    phase="Operations"
    ariaLabel="RF home"
    embedded={embedded}
    moreActions={[{ label: 'Exit RF', onSelect: onExitRf, danger: true }]}
  >
    <TaskHeader>
      <h1>Choose a workflow</h1>
    </TaskHeader>
    <MenuList>
      <MenuButton type="button" onClick={onOpenInbound}>
        <strong>Inbound</strong>
        <span>Receive incoming inventory.</span>
      </MenuButton>
      <MenuButton type="button" disabled>
        <strong>Inventory Management</strong>
        <span>Coming soon</span>
      </MenuButton>
      <MenuButton type="button" disabled>
        <strong>Outbound</strong>
        <span>Coming soon</span>
      </MenuButton>
    </MenuList>
  </RfShell>
)

export const RfHome = ({ embedded = false, onOpenInbound, onExitRf }: RfHomeProps = {}) => {
  const navigate = useNavigate()
  useRfPageTitle('RF Home')
  const openInbound = onOpenInbound ?? (() => navigate('/rf/inbound/'))
  const exitRf = onExitRf ?? (() => navigate('/warehouse/dashboard/'))

  return (
    <>
      <Helmet>
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <meta name="theme-color" content="#4B0092" />
      </Helmet>
      <RfHomeScreen embedded={embedded} onOpenInbound={openInbound} onExitRf={exitRf} />
    </>
  )
}

interface RfInboundProps {
  embedded?: boolean
  onBack?: () => void
  onOpenReceiving?: () => void
  onExitRf?: () => void
}

export const RfInbound = ({ embedded = false, onBack, onOpenReceiving, onExitRf }: RfInboundProps = {}) => {
  const navigate = useNavigate()
  useRfPageTitle('Inbound RF')
  const returnHome = onBack ?? (() => navigate('/rf/'))
  const openReceiving = onOpenReceiving ?? (() => navigate('/rf/inbound/receiving/'))
  const exitRf = onExitRf ?? (() => navigate('/warehouse/dashboard/'))

  return (
    <RfShell
      title="Inbound"
      phase="RF Home"
      ariaLabel="Inbound RF workflows"
      embedded={embedded}
      onBack={returnHome}
      moreActions={[{ label: 'Exit RF', onSelect: exitRf, danger: true }]}
    >
      <TaskHeader>
        <h1>Choose an inbound workflow</h1>
      </TaskHeader>
      <MenuList>
        <MenuButton type="button" onClick={openReceiving}>
          <strong>Receiving</strong>
          <span>Identify a shipment and receive its inventory.</span>
        </MenuButton>
      </MenuList>
    </RfShell>
  )
}

export const RfApp = () => (
  <Routes>
    <Route index element={<RfHome />} />
    <Route path="inbound" element={<RfInbound />} />
    <Route path="inbound/receiving/*" element={<MobileReceiving />} />
    <Route path="*" element={<Navigate to="/rf/" replace />} />
  </Routes>
)
